import { useId, useState } from "react";
import { useApi, useApiMutation } from "../../api/hooks";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { useToast } from "../../components/Toasts";

type Format = "xlsx" | "csv";

const FORMATS: { value: Format; label: string; hint: string }[] = [
  { value: "xlsx", label: "Excel (.xlsx)", hint: "One sheet of vendors, one of the FBR files it relies on." },
  { value: "csv", label: "CSV (.csv)", hint: "For accounting systems; the same columns, UTF-8." },
];

/**
 * The Vendors page "Export": every active vendor as this page shows it, with each status's FBR file
 * (published date and SHA-256), the s.153(1)(a) rate with its rate-table version, and the last status
 * change. Built by the worker from stored data, so the file carries its own content hash; this button
 * waits for it and offers the download (it is also kept under Settings → Your data).
 */
export function ExportVendorList() {
  const [open, setOpen] = useState(false);
  const [format, setFormat] = useState<Format>("xlsx");
  const [exportId, setExportId] = useState<string | null>(null);
  const create = useApiMutation("exports.create", ["exports.list"]);
  const toast = useToast();
  const name = useId();
  const poll = useApi("exports.get", exportId ? { params: { id: exportId } } : undefined, {
    enabled: Boolean(exportId),
    refetchInterval: (q) => {
      const s = q.state.data?.export.status;
      return s === "ready" || s === "failed" ? false : 1500;
    },
  });
  const ex = exportId ? poll.data?.export : undefined;
  const label = (ex?.format ?? format).toUpperCase();

  return (
    <>
      {exportId && (
        <span className="row" style={{ gap: "var(--space-2)" }} role="status" aria-live="polite">
          {ex?.status === "ready" && ex.download_url ? (
            <a className="btn" href={ex.download_url} download>
              Download {label}
            </a>
          ) : ex?.status === "failed" ? (
            <span className="pill danger">Export failed: {ex.failure ?? "try again"}</span>
          ) : (
            <span className="subtle">Preparing {label}…</span>
          )}
        </span>
      )}
      <button className="btn" type="button" onClick={() => setOpen(true)}>
        Export
      </button>
      <ConfirmDialog
        open={open}
        title="Export vendor list"
        confirmLabel="Prepare export"
        busy={create.isPending}
        onCancel={() => setOpen(false)}
        onConfirm={() =>
          create.mutate(
            { body: { kind: "vendor_list", params: { format } } },
            {
              onSuccess: (r) => {
                setExportId(r.export_id);
                setOpen(false);
                toast.success("Vendor list export requested. The download appears here when it is ready.");
              },
              onError: (err) => toast.error(err),
            },
          )
        }
      >
        <p>
          Every active vendor with its identifiers, payee type, income-tax and sales-tax ATL status (on the
          list or not), the FBR file each status came from with its date and SHA-256, the s.153(1)(a) rate and
          rate-table version, and the last status change.
        </p>
        <fieldset className="stack" style={{ gap: "var(--space-2)", border: 0, padding: 0, margin: 0 }}>
          <legend className="label">Format</legend>
          {FORMATS.map((f) => (
            <label key={f.value} className="row" style={{ gap: "var(--space-2)", alignItems: "baseline" }}>
              <input
                type="radio"
                name={name}
                value={f.value}
                checked={format === f.value}
                onChange={() => setFormat(f.value)}
              />
              <span>
                {f.label} <span className="subtle">{f.hint}</span>
              </span>
            </label>
          ))}
        </fieldset>
      </ConfirmDialog>
    </>
  );
}
